import React, { useEffect, useContext, useState } from "react";
import { Input, Modal, ModalBody } from "@windmill/react-ui";
import { SidebarContext } from "context/SidebarContext";
import LabelArea from "components/form/LabelArea";
import InputSearchDate from "components/form/InputSearchDate";
import CommissionPaymentServices from "services/CommssionPaymentServices";
import { notifyError, notifySuccess } from "utils/toast";

const CommissionPaymentModal = ({
  isOpen,
  onClose,
  title,
  requestAmount,
  roundedOffDifference,
  id,
  setIsUpdate,
}) => {
  const [utr, setUtr] = useState("");
  const [remark, setRemark] = useState("");
  const [searchstartDate, setSearchStartDate] = useState(new Date());
  const [paymentType, setPaymentType] = useState(null);
  const [payableAmount, setPayableAmount] = useState(requestAmount);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setPayableAmount(requestAmount);
  }, [requestAmount]);

  useEffect(() => {
    if (isOpen) {
      setPaymentType(null);
      setSearchStartDate(new Date());
      setRemark("");
      setUtr("");
      setPayableAmount(requestAmount);
      setErrors({});
    }
  }, [isOpen, requestAmount]);

  const handlePaymentSubmit = async (data) => {
    try {
      const response = await CommissionPaymentServices.UpdateCommsionpayment(
        id,
        data,
      );
      notifySuccess(response?.message);
      setIsUpdate(true);
    } catch (err) {
      console.error("Error:", err);
      notifyError(err?.response?.data?.message || "Something went wrong");
    }
  };

  const onSubmit = () => {
    const newErrors = {};
    if (!searchstartDate) newErrors.date = "Payment Date is required.";
    if (!paymentType) newErrors.type = "Payment Type is required.";
    if (paymentType === "Bank Transaction" && !utr.trim())
      newErrors.utr = "UTR / Ref ID is required.";
    if (payableAmount === "" || Number(payableAmount) <= 0) {
      newErrors.amount = "A valid payment amount is required.";
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const updatedData = {
      paid_date: searchstartDate,
      payment_type: paymentType,
      utr: paymentType === "Bank Transaction" ? utr : "",
      remark: remark || "",
      amount: Number(payableAmount) || 0,

      roundedOffDifference: roundedOffDifference,
    };

    handlePaymentSubmit(updatedData);
    onClose();
  };

  const handlePayableAmountChange = (e) => {
    const value = e.target.value;

    if (value === "") {
      setPayableAmount("");
      setErrors((prev) => ({ ...prev, amount: "" }));
      return;
    }

    let numVal = Number(value);

    if (isNaN(numVal) || numVal < 0) {
      numVal = 0;
    } else if (numVal > Number(requestAmount)) {
      numVal = Number(requestAmount);
    }

    setPayableAmount(numVal);
    setErrors((prev) => ({ ...prev, amount: "" }));
  };

  const handleUtrChange = (e) => {
    setUtr(e.target.value);
    if (errors.utr) setErrors((prev) => ({ ...prev, utr: "" }));
  };

  const handlePaymentTypeChange = (e) => {
    setPaymentType(e.target.value);
    if (errors.type) setErrors((prev) => ({ ...prev, type: "" }));
  };

  const handleRemarkChange = (e) => {
    setRemark(e.target.value);
  };

  const handleDateChange = (date) => {
    setSearchStartDate(date);
    if (errors.date) setErrors((prev) => ({ ...prev, date: "" }));
  };

  return (
    <Modal
      className="relative p-5 bg-white dark:bg-gray-700 dark:text-white rounded-lg"
      style={{
        width: "500px",
        height: "auto",
        maxHeight: "80vh",
        overflowY: "auto",
      }}
      isOpen={isOpen}
      onClose={onClose}
    >
      <ModalBody className="custom-modal" style={{ margin: "0" }}>
        <h2 className="text-center font-bold text-lg">{title}</h2>

        <div className="mt-4 flex space-x-4">
          <div className="flex-1">
            <label className="block text-gray-700">Bill Amount:</label>
            <input
              type="text"
              value={requestAmount}
              readOnly
              className="input w-full border border-gray-300 rounded-md p-2 mt-2"
            />
          </div>

          <div className="flex-1">
            <label className="block text-gray-700">Payable Amount:</label>
            <input
              type="number"
              value={payableAmount}
              readOnly={false}
              onChange={handlePayableAmountChange}
              className="input w-full border border-gray-300 rounded-md p-2 mt-2"
            />
            {errors.amount && (
              <p className="text-red-500 text-xs mt-1">{errors.amount}</p>
            )}
          </div>
        </div>

        <div
          className="max-h-96 flex flex-col gap-5 mt-4"
          style={{ maxHeight: "60vh", scrollbarWidth: "thin" }}
        >
          <div className="flex flex-col flex-1">
            <LabelArea label="Payment Date" />
            <InputSearchDate
              value={searchstartDate}
              onChange={handleDateChange}
            />
            {errors.date && (
              <p className="text-red-500 text-xs mt-1">{errors.date}</p>
            )}
          </div>

          <div className="flex flex-col flex-1">
            <LabelArea label="Payment Type" />
            <select
              className="bg-white border border-gray-200 h-10 text-sm block w-full focus:bg-white focus:border-gray focus:ring-1 focus:ring-blue-500 rounded"
              value={paymentType || ""}
              onChange={handlePaymentTypeChange}
            >
              <option value="">Choose</option>
              <option value="Bank Transaction">Net Banking</option>
              <option value="Cash">Cash</option>
            </select>
            {errors.type && (
              <p className="text-red-500 text-xs mt-1">{errors.type}</p>
            )}
          </div>

          {paymentType === "Bank Transaction" && (
            <Input
              className={`border h-10 text-sm block w-full ${
                errors.utr ? "border-red-500" : ""
              } focus:bg-white`}
              type="text"
              placeholder="Enter UTR or Reference ID"
              value={utr}
              onChange={handleUtrChange}
            />
          )}

          {paymentType && (
            <div className="flex flex-col flex-1">
              <LabelArea label="Remark" />
              <Input
                className="border h-10 text-sm block w-full focus:bg-white"
                type="text"
                placeholder="Enter remark"
                value={remark}
                onChange={handleRemarkChange}
              />
            </div>
          )}

          <div className="flex justify-around mt-3">
            <button
              onClick={onClose}
              className="btn btn-secondary px-4 py-2 bg-gray-300 text-gray-700 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              className="btn btn-primary px-4 py-2 bg-green-500 text-white rounded-md"
            >
              Save
            </button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default CommissionPaymentModal;
